import { NavLink } from 'react-router-dom'
import styled, { css } from 'styled-components'
import { breakpoints, colors, fontSizes, fontWeights } from '@/styles'
import { Stack } from '@/components/structure'
import { Label, ParagraphMedium as Description } from '@/components/text'
import { Button } from '@/components/button'
import useMediaQuery from '@/hooks/use-media-query'

type Props = {
  children?: React.ReactNode
}

export const MenuWrapper: React.FC<Props> = ({ children }) => (
  <Stack top={16} gutter={16}>
    {children}
  </Stack>
)

const TitleLabel = styled(Label)`
  display: flex;
  align-items: center;
  font-weight: 600;
`

export const Title: React.FC<Props> = ({ children }) => (
  <TitleLabel fontSize="18px">{children}</TitleLabel>
)

const style = css`
  background: ${colors.white};
  padding: 24px;
  box-shadow: 0px 4px 6px -2px rgba(0, 0, 0, 0.06);
  border-radius: 8px;
  border: 1px solid ${colors.grey88};
  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    padding: 20px;
  }
`

const PlainButton = styled.button<{ disabled?: boolean }>`
  text-decoration: none;
  text-align: left;
  background: none;
  width: 100%;
  border: none;
  padding: 0;
  cursor: pointer;
  ${props =>
    props.disabled &&
    `
    cursor: default;
    pointer-events: none;
  `}
  ${style};
`

const StyledNavLink = styled(NavLink)<{ disabled?: boolean }>`
  ${props =>
    props.disabled &&
    `
    cursor: default;
    pointer-events: none;
  `}
  display: block;
  ${style};
`

const Inner = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  gap: 48px;
  @media only screen and (max-width: ${breakpoints.ipadMiniMax}) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0px;
  }
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 16px 0;
`

const Wrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px 0;
  color: ${colors.regular};
  > div:last-of-type {
    color: ${colors.grey56};
  }
`

const ButtonWrapper = styled.div``

const StyledButton = styled(Button)`
  @media only screen and (max-width: ${breakpoints.ipadMiniMax}) {
    min-width: unset;
    min-height: unset;
    padding: 16px 0 0;
    font-size: ${fontSizes.regular};
    line-height: 20px;
    &:not(:disabled):not([aria-disabled='true']) {
      color: ${colors.link};
      font-weight: ${fontWeights.medium};
    }
  }
`

type StockButtonProps = React.ComponentProps<typeof Button> & {
  theme: string
}

export const MenuButton: React.FC<StockButtonProps> = ({ theme, children, ...rest }) => {
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)

  return (
    <StyledButton theme={displayMobile ? 'text' : theme} size="small" tabIndex={-1} {...rest}>
      {children}
    </StyledButton>
  )
}

type ClickableProps = {
  to?: string
  onClick?: () => void
  disabled?: boolean
}
const Clickable: React.FC<ClickableProps> = ({ to, onClick, disabled, ...rest }) =>
  typeof onClick === 'function' || !to ? (
    <PlainButton disabled={disabled} onClick={onClick} {...rest} />
  ) : (
    <StyledNavLink disabled={disabled} to={to} {...rest} />
  )

type MenuItemProps = {
  children?: React.ReactNode
  button?: React.ReactNode | string
  badge?: React.ReactNode
  disabled?: boolean
  onClick?: () => void
  to?: string
}

const MenuItem: React.FC<MenuItemProps> = ({
  children,
  button,
  badge,
  to,
  onClick,
  disabled,
  ...rest
}) => {
  return (
    <Clickable to={to} onClick={onClick} disabled={disabled} {...rest}>
      <Inner>
        <Content>
          <Wrapper>{children}</Wrapper>
          {badge}
        </Content>
        <ButtonWrapper aria-hidden>{button}</ButtonWrapper>
      </Inner>
    </Clickable>
  )
}

export { MenuItem, Description }
